/**
 * Direct port of the Flutter app's `lib/core/formatting.dart`.
 *
 * The wording and the rounding are kept identical so a session reads the same
 * in the web app as it does on the headband app — a user comparing the two
 * screens should never see a different duration for the same recording.
 */

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function two(value: number): string {
  return value.toString().padStart(2, '0');
}

/** e.g. `September 16, 2026 · 11:51` in the browser's local time zone. */
export function formatSessionDateTime(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return 'Date unavailable';
  const month = MONTH_NAMES[date.getMonth()] ?? '';
  return `${month} ${date.getDate()}, ${date.getFullYear()} · ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** Short form for dense table cells: `16 Sep 2026, 11:51`. */
export function formatSessionDateTimeShort(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '—';
  const month = (MONTH_NAMES[date.getMonth()] ?? '').slice(0, 3);
  return `${two(date.getDate())} ${month} ${date.getFullYear()}, ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** e.g. `5 min 7 sec`, `45 sec`, `1 hr 4 min`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) return 'Duration unavailable';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs} sec`);
  return parts.join(' ');
}

/** Compact clock form for tables: `05:07`, `1:04:33`. */
export function formatDurationClock(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return hours > 0 ? `${hours}:${two(minutes)}:${two(secs)}` : `${two(minutes)}:${two(secs)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Thousands separators for sample counts and similar large integers. */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

export function capitalise(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

/**
 * Display form of a `mode` value.
 *
 * Short codes are acronyms in this dataset ("ai"), and `capitalise` would
 * render them as "Ai". Anything longer is an ordinary word.
 */
export function formatMode(mode: string): string {
  const trimmed = mode.trim();
  if (trimmed === '') return trimmed;
  return trimmed.length <= 3 ? trimmed.toUpperCase() : capitalise(trimmed);
}
